import {Injectable, OnDestroy} from '@angular/core';
import {
  AnalyticOperation,
  AnalyticOperationClassData,
  Chat, Currency, CurrencyExchange,
  getServiceSuppliersResolved,
  KeyValueItem,
  Partner,
  Project,
  Flow,
  Service,
  ServiceOrder,
  ServiceOrderMinimal,
  ServiceOrderTemplateCreate
} from '@app/shared';
import {BehaviorSubject, catchError, finalize, forkJoin, map, Observable, of, shareReplay, Subject, switchMap, tap} from 'rxjs';
import {
  deepClean,
  DocumentService,
  FlowEventService,
  LoadUnitService,
  NavigationService,
  ProjectService,
  setGetParamsById,
  StServiceService
} from '@app/core';
import {AuthService} from '../../../core/services/auth.service';
import {Router} from '@angular/router';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {GlobalValuesService} from '../../../core/services/global-values.service';
import {NzNotificationService} from 'ng-zorro-antd/notification';

type OrderDetail = { isInvoiceOrder: boolean; orderId: number } | undefined;

@Injectable({
  providedIn: 'root'
})
export class ProjectUtilsService implements OnDestroy {

  public projects: Project[] = [];
  currenciesExchange:CurrencyExchange[]=[]
  currencies:Currency[]=[]
  /** Variables de cache **/
  cachedProjects = new Map<number, Project>();
  cachedServices = new Map<number, Service>();
  closeAllDrawers$ = new BehaviorSubject<boolean>(false);
  currentDetailChat$ = new BehaviorSubject<Chat | undefined>(undefined);
  currentChatEntetyName$ = new BehaviorSubject<string>('');
  uncompletedService: Service | undefined;
  uncompletedProject: Project | undefined;
  currentKeyValueItemEditing$: BehaviorSubject<KeyValueItem | null> = new BehaviorSubject<KeyValueItem | null>(null);
  /**
   * El flujo del servicio seleccionado acaba de cambiar.
   *
   * Las pantallas abiertas guardan su propia copia del flujo al abrirse, asi que sin este
   * aviso siguen pintando las etapas viejas hasta que se recarga la pagina.
   */
  serviceFlowUpdated$ = new Subject<Flow>();
  private destroy$: any;

  /**
   * Lista minima de TODAS las SO del servicio seleccionado (id, reference, state).
   *
   * Es la unica fuente fiable para saber cuantas SO tiene un servicio.
   * selectedService.serviceOrders NO sirve para eso: service-order-table lo
   * sobreescribe con la pagina filtrada actual, asi que con un filtro que no
   * devuelve resultados queda en cero aunque el servicio tenga ordenes.
   */
  serviceOrdersByService$ = new BehaviorSubject<ServiceOrderMinimal[]>([]);
  private serviceOrdersByServiceId: number | undefined;
  /** Peticion en curso, para que flow-section y la tabla compartan la misma llamada. */
  private serviceOrdersByServiceRequest$: Observable<ServiceOrderMinimal[]> | undefined;

  constructor(
    private projectService: ProjectService,
    private stServiceService: StServiceService,
    private documentService: DocumentService,
    private flowEventService: FlowEventService,
    private authService: AuthService,
    private router: Router,
    private navigationService: NavigationService,
    private loadUnitService: LoadUnitService,
    private globalValuesService:GlobalValuesService,
    private notificationService: NzNotificationService,
  ) {
    this.globalValuesService.currenciesExchange$.pipe(takeUntilDestroyed())
      .subscribe((value) => this.currenciesExchange = value);
    this.globalValuesService.currencies$.pipe(takeUntilDestroyed())
      .subscribe((value) => this.currencies = value);
  }

  /**
   * Carga la lista minima de SO del servicio y la publica en serviceOrdersByService$.
   * Reutiliza lo ya cargado salvo que se pida forzar (tras editar el flujo, por
   * ejemplo, donde los estados de las SO cambian del lado del backend).
   */
  loadServiceOrdersByService(serviceId: number, force = false): Observable<ServiceOrderMinimal[]> {
    if (!force && this.serviceOrdersByServiceId === serviceId) {
      return of(this.serviceOrdersByService$.value);
    }

    if (!force && this.serviceOrdersByServiceRequest$) {
      return this.serviceOrdersByServiceRequest$;
    }

    this.serviceOrdersByServiceRequest$ = this.stServiceService.getServiceOrdersByServiceId(serviceId).pipe(
      map(rsp => rsp.data || []),
      tap(rows => {
        this.serviceOrdersByServiceId = serviceId;
        this.serviceOrdersByService$.next(rows);
      }),
      catchError(error => {
        console.warn('[ProjectUtils] No se pudieron cargar las SO del servicio.', {serviceId, error});
        return of(this.serviceOrdersByService$.value);
      }),
      finalize(() => this.serviceOrdersByServiceRequest$ = undefined),
      shareReplay({bufferSize: 1, refCount: false})
    );

    return this.serviceOrdersByServiceRequest$;
  }

  /** Total real de SO del servicio seleccionado, sin depender de filtros ni paginacion. */
  get ordersCountInSelectedService(): number {
    return this.serviceOrdersByServiceId === this.selectedService?.id
      ? this.serviceOrdersByService$.value.length
      : 0;
  }

  private _openDetailOrderDrawer = new BehaviorSubject<OrderDetail>(undefined);

  get openDetailOrderDrawer(): Observable<undefined | { isInvoiceOrder: boolean, orderId: number }> {
    return this._openDetailOrderDrawer as Observable<undefined | { isInvoiceOrder: boolean, orderId: number }>
  }

  set openDetailOrderDrawer(value: undefined | { isInvoiceOrder: boolean, orderId: number }) {
    this._openDetailOrderDrawer.next(value);
  }

  private _selectedProject: Project | undefined;

  get selectedProject(): Project | undefined {
    return this._selectedProject;
  }

  set selectedProject(project: Project | undefined) {
    if (project) this.cachedProjects.set(project.id, project);
    this._selectedProject = project;
  }

  /**
   * Refresca desde la API el total planificado del proyecto.
   * El backend lo calcula sumando los servicios activos, asi que no puede
   * calcularse en el front: las rutas filtradas por compania devuelven solo
   * parte de los servicios y la suma quedaria incompleta.
   */
  refreshProjectTotalPlanned(projectId: number): Observable<number | undefined> {
    return this.projectService.getProject(setGetParamsById(projectId)).pipe(
      map((rsp: any) => rsp?.data?.rows?.[0]?.totalPlanned as number | undefined),
      tap((totalPlanned) => {
        const cached = this.cachedProjects.get(projectId);
        if (!cached) return;

        const updated: Project = {...cached, totalPlanned: totalPlanned ?? 0};
        if (this._selectedProject?.id === projectId) {
          // El setter actualiza tambien cachedProjects
          this.selectedProject = updated;
        } else {
          this.cachedProjects.set(projectId, updated);
        }
      }),
      catchError((error) => {
        console.warn('[ProjectUtils] No se pudo refrescar el total planificado del proyecto.', {projectId, error});
        return of(undefined);
      })
    );
  }

  _selectedService: Service | undefined;

  get selectedService(): Service | undefined {
    return this._selectedService;
  }

  set selectedService(service: Service | undefined) {
    //console.log('se actualizo el servicio', service);
    if (service) this.cachedServices.set(service.id, service);
    this._selectedService = service;
  }

  set currentKeyValueItemEditing(value: KeyValueItem | null) {
    this.currentKeyValueItemEditing$.next(value);
  }

  getCurrentKeyValueItemEditing() {
    return this.currentKeyValueItemEditing$ as Observable<KeyValueItem | null>;
  }

  processServiceOrderForMoneyData(serviceOrder: ServiceOrder): {
    planData: AnalyticOperationClassData;
    executedData: AnalyticOperationClassData;
    engagedData: AnalyticOperationClassData;
    totalData: AnalyticOperationClassData;
  } {
    const categories: any = {Planned: [], Executed: [], Engaged: []};
    serviceOrder.analyticOperations?.forEach((ao: AnalyticOperation) => categories[ao.class].push(ao));

    const planData: AnalyticOperationClassData = this.calculateAnalyticOpertationData(categories.Planned);
    const executedData: AnalyticOperationClassData = this.calculateAnalyticOpertationData(categories.Executed);
    const engagedData: AnalyticOperationClassData = this.calculateAnalyticOpertationData(categories.Engaged);

    const incomeTotal = planData.incomeTotal + executedData.incomeTotal + engagedData.incomeTotal;
    const totalMargin = planData.margin + executedData.margin + engagedData.margin
    let totalData: AnalyticOperationClassData = {
      incomeTotal: incomeTotal,
      expensesTotal: planData.expensesTotal + executedData.expensesTotal + engagedData.expensesTotal,
      margin: totalMargin,
      percent: 0
    }

    totalData.percent = totalData.margin < 0 ? (totalData.expensesTotal !== 0 ?(totalData.margin / totalData.expensesTotal) * -100 : 0) : (totalData.incomeTotal !== 0 ? (totalData.margin / incomeTotal) * 100 : 0)

    return {
      planData,
      executedData,
      engagedData,
      totalData,
    }
  }

  calculateAnalyticOpertationData(analyticOperations: AnalyticOperation[]): AnalyticOperationClassData {
    let incomeTotal: number = 0;
    let expensesTotal: number = 0;
    let margin: number = 0;
    let percent: number = 0;

    const idUsd = this.currencies.find(item=>  item.isoCode==='USD')?.id
    const usdExchange = (this.currenciesExchange?.find(item =>
      item.currencyId=== idUsd
    )?.baseRate) || 1;

    if (analyticOperations.length > 0) {
      analyticOperations.forEach(ao => {
        let amountUsd:number
        if(ao.currency?.id===idUsd){
          amountUsd=ao.amount;
        }else {
          amountUsd=ao.amount*usdExchange/(ao.currency?.currentCurrencyExchange?.baseRate ?? 1);
        }

        if (ao.type === 'Ingreso') {
          incomeTotal += amountUsd;
        } else {
          expensesTotal -= amountUsd;
        }
      });

      // Se suma porque desde arriba el valor de expensesTotal es negativo
      margin = incomeTotal + expensesTotal;
      percent = margin < 0 ? (expensesTotal !== 0 ?(margin / expensesTotal) * -100 : 0) : (incomeTotal !== 0 ? (margin / incomeTotal) * 100 : 0);

    }
    // if (analyticOperations.length > 0) {
    //   const amountRequests = analyticOperations.map(ao =>
    //     this.analyticOperationService.calculateAmount(ao.quantity, ao.type)
    //   );
    //
    //   forkJoin(amountRequests).pipe(takeUntil(this.destroy$)).subscribe({
    //     next: (amounts) => {
    //       analyticOperations.forEach((ao, index) => {
    //         const amount = amounts[index];
    //         if (ao.type === 'Ingreso') {
    //           incomeTotal += amount;
    //         } else {
    //           expensesTotal -= amount;
    //         }
    //       });
    //
    //       margin = incomeTotal + expensesTotal;
    //       percent = incomeTotal !== 0 ? (margin / incomeTotal) * 100 : -100;
    //
    //       // Actualizar UI o estado aquí
    //     },
    //     error: (err) => console.error("Error:", err)
    //   });
    // }

    return {
      incomeTotal,
      expensesTotal,
      margin,
      percent,
    }
  }

  getProjectAndServiceData(): Observable<boolean> {
    // Se obtienen los ids del proyecto y del servicio de la URL
    const segments = window.location.pathname.split('/');
    const projectId: number = Number(segments[segments.indexOf('detail') + 1]);
    let serviceId: number | undefined;
    if (segments.indexOf('service') !== -1) {
      serviceId = Number(segments[segments.indexOf('service') + 1]);
    }

    // Se obtienen los datos cacheados del proyecto y del servicio si existen
    const cachedProject: Project | undefined = this.cachedProjects.get(projectId);
    const cachedService: Service | undefined = serviceId ? this.cachedServices.get(serviceId) : undefined;

    // Si los datos cacheados existen se procesa la info y se actualiza el estado global
    // (Se actualizan los datos cacheados sin el Chat)
    if (!serviceId && cachedProject) {
      if (!this.checkPermises(cachedProject)) {
        return of(false);
      }
      this.setBreadcrumb(cachedProject);
      this.selectedProject = cachedProject;

      return of(true);
    } else if (serviceId && cachedProject && cachedService) {
      if (!this.checkPermises(cachedProject, cachedService)) {
        return of(false);
      }
      this.setBreadcrumb(cachedProject, cachedService);
      this.selectedProject = cachedProject;
      this.selectedService = cachedService;

      return of(true);
    }

    // Si no existen datos cacheados se obtienen los datos de la API
    // Se configuran los requests del Project
    let requests: {
      [key: string]: Observable<any>; // Firma de índice para permitir propiedades dinámicas
      project: Observable<any>;
      containers: Observable<any>;
    } = {
      project: this.projectService.getProject(setGetParamsById(projectId)),
      containers: this.projectService.getProjectContainer({
        startRow: 0,
        endRow: 10000,
        sortProperty: 'id',
        sortDirection: 'asc',
        filterProperty: 'projectId',
        filterValue: projectId.toString(),
      }),
    }

    // Si estamos en ServiceDetail tambien se configura para obtener los datos del servicio
    if (serviceId) {
      requests['service'] = this.stServiceService.getServiceById(serviceId);
      requests['serviceDocuments'] = this.documentService.getDocument({
        startRow: 0,
        endRow: 100,
        sortDirection: 'asc',
        sortProperty: 'id',
        entityId: serviceId,
        entityType: 'Service',
      }).pipe(
        catchError((error) => {
          console.warn('[ProjectDetail] No se pudieron cargar documentos del servicio. Se continua sin documentos.', {
            serviceId,
            error,
          });
          return of({data: {rows: []}});
        })
      );
      requests['flow'] = this.flowEventService.getFlow({
        startRow: 0,
        endRow: 100,
        sortDirection: 'asc',
        sortProperty: 'id',
        filterProperty: 'serviceId',
        filterValue: serviceId.toString(),
      })
      requests['template'] = this.stServiceService.getServiceOrderTemplate({
        startRow: 0,
        endRow: 1,
        sortDirection: 'asc',
        sortProperty: 'id',
        filterProperty: 'serviceId',
        filterValue: serviceId.toString(),
      })
    } else {
      // Si no hay Service se hace la llamada para obtener los projectDocuments pq la API de Documents no admite peticiones concurrentes
      requests['projectDocuments'] = this.documentService.getDocument({
        startRow: 0,
        endRow: 100,
        sortDirection: 'asc',
        sortProperty: 'id',
        entityId: projectId,
        entityType: 'Project',
      }).pipe(
        catchError((error) => {
          console.warn('[ProjectDetail] No se pudieron cargar documentos del proyecto. Se continua sin documentos.', {
            projectId,
            error,
          });
          return of({data: {rows: []}});
        })
      )
    }

    // Se hacen las peticiones que se hayan configurado
    return forkJoin(requests).pipe(
      switchMap((rsp: any) => {
        // Configurar datos del proyecto
        const project: Project = rsp.project.data.rows[0];
        project.projectContainers = rsp.containers.data?.rows || [];
        let service: Service | undefined;

        // Función modificada para crear templates
        const createTemplatesIfNeeded = (): Observable<void> => {
          let loadUnitTemplate: any;
          if (!rsp.template?.data?.rows?.[0]) {
            return this.loadUnitService.createLoadUnitTemplate({packageTypeId: 1}).pipe(
              switchMap(luTRsp => {
                loadUnitTemplate = luTRsp.data;
                const soTemplateParams: ServiceOrderTemplateCreate = {
                  loadUnitTemplateId: luTRsp.data.id,
                  createdAt: new Date().toISOString(),
                  serviceId: service!.id,
                  isActive: true,
                  differentiatedBlock: false,
                  originContactId: service!.originContactId,
                  originId: service!.originId,
                  destinationContactId: service!.destinationContactId,
                  destinationId: service!.destinationId,
                };
                return this.stServiceService.createServiceOrderTemplate(deepClean(soTemplateParams));
              }),
              tap(soTRsp => {
                service!.serviceOrderTemplate = soTRsp.data;
                if (service!.serviceOrderTemplate) service!.serviceOrderTemplate.loadUnitTemplate = loadUnitTemplate;
              }),
              map(() => undefined) // Emite señal de completitud
            );
          }
          return of(undefined); // No necesita crear template
        };

        // Si estamos en ServiceDetail
        if (serviceId) {
          service = rsp.service.data.rows[0];
          if (!rsp.flow?.data?.rows?.length) {
            // SOU-525: Servicio sin Flujo — no se puede abrir su ficha (ni por navegación ni por URL directa)
            this.notificationService.warning(
              'Servicio no disponible',
              'Este servicio tiene un problema de configuración (sin Flujo asignado) y no se puede abrir.',
              // Abajo, como el resto de avisos: arriba a la derecha tapaba el buscador global (SOU-523).
              {nzPlacement: 'bottomRight'}
            );
            this.router.navigate(['/projects']);
            return of({check: false, project, service: undefined, rsp});
          }
          service!.flow = rsp.flow.data && rsp.flow.data.rows && rsp.flow.data.rows.length > 0 ? rsp.flow.data.rows[0] : undefined;
          service!.serviceOrderTemplate = rsp.template?.data?.rows?.[0];
          service!.documents = rsp.serviceDocuments?.data?.rows || [];
          project.documents = undefined;

          // Verificar y crear templates si es necesario
          return createTemplatesIfNeeded().pipe(
            map(() => ({check: true, project, service, rsp}))
          );
        } else {
          project.documents = rsp.projectDocuments?.data?.rows || [];
          return of({check: true, project, service, rsp});
        }
      }),
      switchMap(({check, project, service}) => {
        // Si aqui arriba ya se decidio que no se puede abrir (Servicio sin Flujo),
        // esa decision manda: volver a evaluarla daria true y la pantalla cargaria sin Servicio.
        if (!check) {
          return of({check: false, project, service});
        }

        // Revisar permisos
        if (!this.checkPermises(project, service)) {
          return of({check: false, project, service});
        }

        // Configurar breadcrumb
        this.setBreadcrumb(project, service);

        return of({check: true, project, service});
      }),
      tap(({project, service, check}) => {
        // Asignar SOLO después de todas las operaciones
        this.selectedProject = project;
        this.selectedService = service; // Service con template actualizado si se creó
      }),
      map(({check}) => check), // Convertir a resultado booleano final
      catchError((e) => {
        console.error(e);
        return of(false);
      })
    );
  }

  checkPermises(project: Project, service?: Service): boolean {
    const user = this.authService.loggedUser$.value;
    if (!service) {
      if (!user?.viewUnassignedProjectsAndServices) {
        const userPartnerId = user?.partner.id;
        if (!userPartnerId || !project.services?.some(s => {
          const { supplierIds } = getServiceSuppliersResolved(s);
          return supplierIds.includes(userPartnerId) || s.supplierId === userPartnerId;
        })) {
          return false;
        }
      }
    } else {
      if (!user?.viewUnassignedProjectsAndServices) {
        const userPartnerId = user?.partner.id;
        if (!userPartnerId) {
          return false;
        }
        const { supplierIds } = getServiceSuppliersResolved(service);
        const isUserSupplier = supplierIds.includes(userPartnerId) || service.supplierId === userPartnerId;
        if (!isUserSupplier) {
          return false;
        }
      }
    }
    return true;
  }

  getPartnersFromProject(project: Project): Partner[] {
    const partnerIds = project.partnerIds || (project.partnerId ? [project.partnerId] : []);
    return partnerIds.map(id => this.globalValuesService.partnerlist.find(p => p.id === id)).filter(Boolean) as Partner[];
  }

  setBreadcrumb(project: Project, service?: Service) {
    if (!service) {
      this.navigationService.setBreadcrumb([
        {label: 'Proyectos', navigate: '/projects'},
        {label: project.name}
      ]);
    } else {
      this.navigationService.setBreadcrumb([
        {label: 'Proyectos', navigate: '/projects'},
        {
          label: service.project!.name,
          navigate: '/projects/detail/' + service!.projectId
        },
        {label: service!.name}
      ]);
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
